import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import usePageStore from '@monorepo/shared/src/graph/state/query-hooks/use-page-store';
import WithQuery from 'components/layout/WithQuery';
import useAssignmentsStore from 'graph/state/query-hooks/use-assignments-store';
import { BookType } from '@monorepo/shared/src/graph/schema-generated-types-student';
import { BookCoverProps } from 'types';
import AssignmentCover from './AssignmentCover';
import { AssignmentWithCover, ASSIGNMENT_STATUSES } from '../types';
import {
  fetchAndSetAssignmentsCovers,
  getFirstIncompleteAssignment,
  isCompletedStatus,
} from '../util';
import useAudioInstructions from 'state/hooks/use-audio-instructions';
import useAppLayout from 'state/hooks/use-app-layout';
import { MenuPlacementType, STUDENT_APP_BUTTON_SIZES } from '@monorepo/shared/src/types';
import { disabled } from '@monorepo/shared/src/styles/sharedStyles';
import Carousel from 'components/common/Carousel';

const AssignmentsCarousel: React.FC = () => {
  const { menuPlacement, offset } = useAppLayout();
  const marginTop =
    menuPlacement === MenuPlacementType.ASIDE ? STUDENT_APP_BUTTON_SIZES.default + 125 : 125;
  const classes = styles({ marginTop, offset });

  const [
    { assignments, assignmentSelectSound, isLoading: loadingAssignments, error: assignmentsError },
  ] = useAssignmentsStore();

  const [{ books, loading: loadingLetterBooks, error: letterBooksError }] = usePageStore(
    BookType.LETTER_BOOK
  );
  const [assignmentsCovers, setAssignmentsCovers] = useState<
    AssignmentWithCover[] | { cover: BookCoverProps }[] | unknown[]
  >([]);
  const [loadingAssignmentsCovers, setLoadingAssignmentsCovers] = useState<boolean>(true);

  const setAssignmentsWithCovers = async (books, assignments) => {
    const assignmentsWithCovers = await fetchAndSetAssignmentsCovers(books, assignments);

    setAssignmentsCovers(assignmentsWithCovers);
    setLoadingAssignmentsCovers(() => false);
  };

  useEffect(() => {
    if (assignments && books && books.length && !loadingAssignments && !loadingLetterBooks) {
      setAssignmentsWithCovers(books, assignments);
    }
  }, [assignments, books, loadingAssignments, loadingLetterBooks]);

  useAudioInstructions(
    assignmentSelectSound,
    !loadingLetterBooks &&
      !loadingAssignmentsCovers &&
      !assignmentsError &&
      !letterBooksError &&
      assignmentsCovers.length
  );

  const renderAssignmentCovers = () => {
    return assignmentsCovers.map((assignment, index) => {
      if (!assignment.id) {
        return (
          <div key={`unassigned-cover-${index}`} className={classes.assignmentItem}>
            <AssignmentCover
              assignment={assignment}
              showLetterBookBadge={false}
              status={ASSIGNMENT_STATUSES.INCOMPLETE}
              style={disabled}
            />
          </div>
        );
      }

      return (
        <div key={`${assignment.id}`} className={classes.assignmentItem}>
          <Link
            className={classes.assignmentLink}
            id={`${assignment.id}`}
            to={{
              pathname: `${location.pathname}/${assignment.id}`,
            }}
          >
            <AssignmentCover
              assignment={assignment}
              showLetterBookBadge
              status={assignment.status}
              style={isCompletedStatus(assignment.status) ? disabled : undefined}
            />
          </Link>
        </div>
      );
    });
  };

  const firstIncompleteAssignment = getFirstIncompleteAssignment(assignmentsCovers);

  return (
    <WithQuery
      loading={loadingAssignments || loadingAssignmentsCovers || loadingLetterBooks}
      error={
        assignmentsError ||
        letterBooksError ||
        (!loadingAssignmentsCovers && !assignmentsCovers.length)
      }
    >
      <div className={classes.carouselContainer}>
        <Carousel
          className={classes.aditionalCarouselClass}
          firstAssignmentId={firstIncompleteAssignment?.id}
        >
          {renderAssignmentCovers()}
        </Carousel>
      </div>
    </WithQuery>
  );
};
const styles = makeStyles((theme) => ({
  carouselContainer: {
    height: '100%',
    marginLeft: ({ offset }: any) => theme.typography.pxToRem(-offset.left),
    marginRight: ({ offset }: any) => theme.typography.pxToRem(-offset.left),
  },
  aditionalCarouselClass: {
    height: ({ marginTop }: any) => `calc(100% - ${theme.typography.pxToRem(marginTop)})`,
    marginTop: ({ marginTop }: any) => theme.typography.pxToRem(marginTop),
  },
  assignmentItem: {
    display: 'inline-flex',
    flex: '0 0 auto',
    height: '100%',
    aspectRatio: '3 / 4',
    verticalAlign: 'top',
  },
  assignmentLink: {
    display: 'flex',
    height: '100%',
    width: '100%',
  },
}));

export default AssignmentsCarousel;
